import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  DataApiResponse,
  EditUserEducationHistoryListModel,
  EducationCategoryModel,
  EducationLevelModel,
  EducationModel,
  Option,
  SchoolModel,
  UserEducationHistoryModel,
} from '@digilize/shared/definitions/src';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class EducationService {
  private readonly http = inject(HttpClient);
  private countriesOptions$ = new BehaviorSubject<Option[]>([]);
  private countriesLoaded = false;

  getEducationMy = () => this.http.get<DataApiResponse<UserEducationHistoryModel[]>>('/api/v1.0/my/education');

  putEducationMy = (req: EditUserEducationHistoryListModel) =>
    this.http.put<DataApiResponse<UserEducationHistoryModel[]>>('/api/v1.0/my/education', req);

  getEducation = (searchValue: string) =>
    this.http.get<DataApiResponse<EducationModel[]>>('/api/v1.0/education/', {
      params: {
        'search.value': searchValue,
        length: 15,
      },
    });

  getSchoolsData = (searchValue: string, code: string) =>
    this.http.get<DataApiResponse<SchoolModel[]>>('/api/v1.0/education/schools', {
      params: {
        'search.value': searchValue,
        country: code,
        length: 15,
      },
    });

  getCategoriesData = (searchValue: string, code: string) =>
    this.http.get<DataApiResponse<EducationCategoryModel[]>>('/api/v1.0/education/categories', {
      params: {
        'search.value': searchValue,
        country: code,
        length: 15,
      },
    });

  getLevels = (searchValue: string, code: string) =>
    this.http.get<DataApiResponse<EducationLevelModel[]>>('/api/v1.0/education/levels', {
      params: {
        'search.value': searchValue,
        country: code,
        length: 15,
      },
    });

  getCategories = () => this.http.get<DataApiResponse<EducationCategoryModel[]>>('/api/v1.0/education/categories');

  getCountries = (lang: string) =>
    this.http.get<DataApiResponse<CountryOption[]>>('/api/v1.0/countries', {
      params: {
        Language: lang,
      },
    });

  getCountriesOptions(lang: string): Observable<Option[]> {
    if (this.countriesLoaded) {
      return of(this.countriesOptions$.value);
    }
    return this.getCountries(lang).pipe(
      map((data: any) =>
        (data.data ?? data).map((item) => ({
          id: item.alpha2_code,
          text: item.common_name,
          val: item.alpha2_code,
        }))
      ),
      tap((options) => {
        this.countriesOptions$.next(options);
        this.countriesLoaded = true;
      })
    );
  }
}

export interface CountryOption {
  landcode: string;
  common_name: string;
  alpha2_code: string;
}
